import axios, { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import getCollectiveHeaders, { getAuth0StaffHeaders } from './getCollectiveHeaders';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import isPageHidden from 'utils/analytics/pageVisibility';
import config from 'modules/common/config';
import { refreshAccessToken, isAccessTokenExpiringSoon } from 'modules/Auth/AuthProvider/tokenRefresh';
import { forceLogoutRedirect } from 'modules/Auth/AuthProvider/forceLogout';
import { auth0AuthorizationHeader, renewRejectedAuth0Bearer } from 'modules/Auth/auth0/tokenBridge';
import LOGOUT_EXEMPT_ENDPOINTS from 'modules/Auth/AuthProvider/logoutExemptEndpoints';
import { bookkeepingCredentialErrorNotice } from 'services/bookkeeping/utils/credentialErrorNotice';

// Endpoints that skip the proactive token-refresh check in the request
// interceptor. Only the rest-auth endpoints belong here — ai/ endpoints still
// need a fresh access token, they just shouldn't force-logout on a 401.
const AUTH_ENDPOINTS = [
    'rest-auth/login/',
    'rest-auth/login/code/',
    'rest-auth/login/resend-code/',
    'rest-auth/refresh-token/',
    'rest-auth/logout/',
];

/**
 * A request the browser dropped while the tab was hidden or closing, such as a prefetch killed when
 * the member switched tabs. Nobody saw it fail, so it is not reported; it still rejects.
 */
const isBackgroundNetworkFailure = (error: unknown): boolean =>
    axios.isAxiosError(error) && !error.response && error.code === 'ERR_NETWORK' && isPageHidden();

const collectiveApi = axios.create({
    baseURL: config.djangoURL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
});

collectiveApi.interceptors.request.use(async (cfg) => {
    const isAuthEndpoint = AUTH_ENDPOINTS.some((ep) => cfg.url?.includes(ep));
    const { skipTokenCheck } = cfg;

    // tokenBridge decides between the Django credential and the Auth0 bearer; null takes
    // the Django path below unchanged. Auth0's SDK owns its expiry and refresh, so the
    // proactive Django refresh is skipped for it.
    if (!isAuthEndpoint) {
        const auth0Authorization = await auth0AuthorizationHeader();
        if (auth0Authorization) {
            cfg.headers.set(getCollectiveHeaders());
            cfg.headers.set('Authorization', auth0Authorization);
            return { ...cfg, auth0Bearer: true };
        }
    }

    // Proactively refresh token before non-auth requests if it's expiring within 60s
    // isAccessTokenExpiringSoon handles both expired tokens (negative time) and soon-to-expire tokens
    if (!isAuthEndpoint && !skipTokenCheck && isAccessTokenExpiringSoon()) {
        const refreshed = await refreshAccessToken();
        if (!refreshed && isAccessTokenExpiringSoon(0)) {
            // Access token is expired AND refresh failed — session is over.
            // refreshAccessToken() already short-circuits without an API call
            // if the refresh token is expired, so no doomed request was made.
            forceLogoutRedirect();
            return Promise.reject(new axios.Cancel('Session expired'));
        }
    }

    cfg.headers.set(getCollectiveHeaders());
    // An Auth0 staffer's impersonation: the member's Django token above, the staffer's Auth0 token here.
    cfg.headers.set(await getAuth0StaffHeaders());
    return cfg;
});

export const IMPERSONATION_READ_ONLY_CODE = 'IMPERSONATION_READ_ONLY';

function isImpersonationReadOnly(error: unknown): error is { response: { data: { error: { message: string } } } } {
    if (!axios.isAxiosError(error) || error.response?.status !== 403) {
        return false;
    }
    const body = error.response.data?.error;
    return body?.code === IMPERSONATION_READ_ONLY_CODE && typeof body?.message === 'string';
}

/**
 * A 401: retry once after refreshing the token, otherwise log out. Resolves rather than rejecting, so
 * callers that destructure the response keep working after a forced logout.
 */
async function recoverFromUnauthorized(error: AxiosError) {
    // Rethrow refresh-token 401s so refreshToken() in useAuthHandlers
    // can detect the failure in its catch block and trigger logout properly.
    // Swallowing it would cause refreshToken() to return true (success).
    if (error.config?.url?.includes('rest-auth/refresh-token/')) {
        throw error;
    }

    const shouldIgnore401Error = LOGOUT_EXEMPT_ENDPOINTS.some((ep) => error.config?.url?.includes(ep));

    // One bounded retry: a request can race a token that goes stale before it's
    // sent (e.g. after a long idle period). retriedAfterRefresh stops a second
    // 401 from retrying again — that means the session is actually dead.
    if (!shouldIgnore401Error && !error.config?.retriedAfterRefresh) {
        const refreshed = error.config?.auth0Bearer
            ? await renewRejectedAuth0Bearer(error.response?.data)
            : await refreshAccessToken();
        if (refreshed) {
            // A 401 on the retry signs out in its own pass through this interceptor. Any other
            // failure on it (a 5xx, the network, a token the SDK cannot produce right now, the
            // view's own 4xx) says nothing about an Auth0 session, so it goes to the caller.
            if (error.config?.auth0Bearer) {
                return collectiveApi({ ...error.config, retriedAfterRefresh: true });
            }
            try {
                return await collectiveApi({ ...error.config, retriedAfterRefresh: true });
            } catch {
                // Retry didn't cleanly succeed (401 again, network error, etc.) — fall
                // through below and treat it exactly like the original 401.
            }
        }
    }

    if (!shouldIgnore401Error) {
        forceLogoutRedirect();
    }

    // Stop this error so that it won't bubble up to the top scope
    // Added data in return to fix deconstruction usage in the app
    return {
        data: { ...((error.response?.data as object | undefined) ?? {}), hasErrors: true },
        headers: error.response?.headers,
        status: error.response?.status,
    };
}

/**
 * The backend refuses every write made while impersonating. Explain it once here, whatever the screen,
 * rather than each screen reporting it as its own failure.
 */
function explainImpersonationRefusal(error: unknown) {
    if (isImpersonationReadOnly(error)) {
        enqueueSnackbar(error.response.data.error.message, {
            variant: 'warning',
            key: IMPERSONATION_READ_ONLY_CODE,
            preventDuplicate: true,
        });
    }
}

collectiveApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (axios.isCancel(error)) {
            throw error;
        }

        const domainErrorHandler = error?.config?.domainErrorHandler;

        if (!domainErrorHandler?.isExpected?.(error) && !isBackgroundNetworkFailure(error)) {
            const method = (error?.config?.method || 'UNKNOWN').toUpperCase();
            const url = error?.config?.url || 'unknown';
            sendDatadogRumError(`API ${method} ${url} failed (${error?.response?.status || 'network'})`, error, {
                url,
                method,
                status: error?.response?.status,
            });
        }

        if (axios.isAxiosError(error) && error.response?.status === 401) {
            return recoverFromUnauthorized(error);
        }
        explainImpersonationRefusal(error);
        // TODO(AP-3200): Domain-specific, and it can only recognise this failure by matching
        // bookkeeping's error bodies. Replace it with a generic handler once the backend returns
        // these errors in a shared format.
        bookkeepingCredentialErrorNotice(error);
        try {
            domainErrorHandler?.onError?.(error);
        } catch (handlerError) {
            // A faulty domain hook must not replace the rejection the caller is waiting for.
            sendDatadogRumError('Domain error handler onError threw', handlerError, { url: error?.config?.url });
        }
        throw error;
    }
);

export default collectiveApi;
